import { z } from 'zod';
import { Instant, problemDetails } from './common.js';
// Доменные коды ошибок записи.
//
// Зачем перечисление, а не текст: на API смотрят витрина, кабинет, боты и мобилка,
// и каждому нужно отличить «слот только что заняли» (показать соседние окна и обновить
// сетку) от «клиент в чёрном списке» (предложить позвонить администратору). По HTTP-коду
// это неразличимо — оба 409. Разбирать русский текст сообщения клиенты не должны,
// а текст PostgreSQL наружу не уходит никогда: в нём имена таблиц и чужие ПД.
// Префикс пространства имён для поля type из RFC 9457. Ссылка стабильна и документируема:
// именно её клиент сравнивает, а не заголовок title, который переводится и переписывается.
export const PROBLEM_TYPE_PREFIX = 'https://planerius.ru/problems/';
/**
 * Коды доменных отказов записи. Отказ — это не сбой: система работает правильно,
 * просто операция запрещена правилами салона или проиграна в гонке за слот.
 */
export const DomainErrorCode = z.enum([
    // Слот заняли между показом сетки и подтверждением. Прямое следствие того,
    // что кеш слотов иногда врёт (ADR-031), и единственно честный ответ на SQLSTATE 23P01.
    'slot_taken',
    // Удержание истекло или уже израсходовано: клиент думал дольше, чем hold_ttl_seconds.
    'hold_expired',
    // Визит начинается раньше минимального лага филиала — «через пять минут» мастер
    // не успеет увидеть запись (GAPS C13).
    'booking_too_soon',
    // Дата выходит за горизонт онлайн-записи филиала: слоты на два года вперёд не продаём.
    'booking_beyond_horizon',
    // Клиент в чёрном списке САЛОНА и лишён онлайн-записи. Сквозного чёрного списка
    // по платформе нет: согласие клиент давал салону, а не нам (ADR-004).
    'client_blacklisted',
    // Превышен лимит одновременно активных записей — защита от «забронировал весь день
    // на всякий случай» (booking_rules.max_active_appointments).
    'active_limit_reached',
    // Отмена позже разрешённого срока: сработал cancel_deadline_minutes.
    'cancel_deadline_passed',
    // Перенос позже разрешённого срока: сработал reschedule_deadline_minutes.
    'reschedule_deadline_passed',
]);
// Код → значение поля type. Дефисы вместо подчёркиваний, потому что это часть URL,
// а не идентификатор в коде.
export function problemTypeOf(code) {
    return `${PROBLEM_TYPE_PREFIX}${code.replaceAll('_', '-')}`;
}
// Код → HTTP-статус. 409 у всех конфликтов состояния, 403 у запретов по правилам салона:
// повтор первых иногда имеет смысл, повтор вторых — никогда.
export const DOMAIN_ERROR_STATUS = {
    slot_taken: 409,
    hold_expired: 409,
    booking_too_soon: 422,
    booking_beyond_horizon: 422,
    client_blacklisted: 403,
    active_limit_reached: 409,
    cancel_deadline_passed: 403,
    reschedule_deadline_passed: 403,
};
/**
 * Тело доменной ошибки: RFC 9457 плюс машинный код и минимум контекста.
 *
 * Контекст ровно тот, что нужен интерфейсу для внятного сообщения, и ни полем больше:
 * ошибка уходит в том числе анонимному посетителю витрины, и «лимит записей исчерпан,
 * вот они» раскрыло бы визиты чужого человека тому, кто просто знает номер телефона.
 */
export const domainProblemDetails = problemDetails.extend({
    code: DomainErrorCode,
    // Для cancel_deadline_passed / reschedule_deadline_passed — момент, после которого
    // стало нельзя; для booking_too_soon — момент, начиная с которого уже можно.
    deadlineAt: Instant.optional(),
    // Для active_limit_reached — сам лимит салона. Список записей сюда не кладётся.
    limit: z.number().int().positive().optional(),
});
//# sourceMappingURL=errors.js.map